import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { KeyRound, Plus } from 'lucide-react';
import { api } from '../../api';
import { Modal } from '../../componentes/Modal';

interface Token {
  id: string; nome: string; prefixo: string; escopos: string[]; ips_permitidos: string[] | null; ativo: boolean;
  criado_em: string; ultimo_uso_em: string | null; ultimo_ip: string | null; revogado_em: string | null; criado_por: string | null; chamados: number;
}
const NOME_ESCOPO: Record<string, string> = { 'chamados:registrar': 'Registrar chamado', 'chamados:consultar': 'Consultar situação do chamado' };
const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/** Tokens para sistemas (n8n/WhatsApp, Maza-Remote, Zabbix) registrarem chamado. Só admin. */
export function TokensServico() {
  const [d, setD] = useState<{ itens: Token[]; escopos: string[] } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const [criado, setCriado] = useState<string | null>(null);
  const carregar = useCallback(() => { api<{ itens: Token[]; escopos: string[] }>('/tokens-servico').then(setD).catch((e) => setErro(e.message)); }, []);
  useEffect(() => { carregar(); }, [carregar]);

  async function revogar(t: Token) {
    if (!window.confirm(`Revogar "${t.nome}"? O sistema que usa este token para de conseguir registrar chamados na hora.`)) return;
    try { await api(`/tokens-servico/${t.id}/revogar`, { metodo: 'POST' }); carregar(); }
    catch (e) { setErro(e instanceof Error ? e.message : 'Não foi possível revogar.'); }
  }

  return (
    <section className="secao">
      <div className="secao-topo">
        <h2>Tokens de serviço</h2>
        <button className="btn-fantasma" onClick={() => setNovo(true)}><Plus size={16} aria-hidden /> Novo token</button>
      </div>
      <p className="nota">Para sistemas abrirem chamado sozinhos: o n8n com a conversa do WhatsApp, o Maza-Remote/Zabbix com o alerta. Endereço: <code>https://ops.vivium.com.br/api/integracao/v1/chamados</code>. Um token por sistema; revogue o que não usa mais.</p>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {d && (d.itens.length === 0 ? <p className="nota">Nenhum token criado.</p> : (
        <div className="cartao tabela-cartao rolagem-x">
          <table className="tabela">
            <thead><tr><th>Sistema</th><th>Pode</th><th>Último uso</th><th className="num">Chamados</th><th></th></tr></thead>
            <tbody>
              {d.itens.map((t) => (
                <tr key={t.id} className={t.ativo ? '' : 'inativo'}>
                  <td><strong>{t.nome}</strong><small className="mono">{t.prefixo}…{t.ips_permitidos?.length ? ` · só de ${t.ips_permitidos.join(', ')}` : ''}</small></td>
                  <td>{t.escopos.map((e) => NOME_ESCOPO[e] ?? e).join(' · ')}</td>
                  <td>{t.ultimo_uso_em ? dataHora(t.ultimo_uso_em) : 'nunca'}<small>{t.ultimo_ip ?? ''}</small></td>
                  <td className="num">{t.chamados}</td>
                  <td>{t.ativo ? <button className="btn-link" onClick={() => revogar(t)}>revogar</button> : <span className="situacao s-na">revogado {t.revogado_em ? dataHora(t.revogado_em) : ''}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <Modal titulo={criado ? 'Token criado' : 'Novo token de serviço'} aberto={novo} aoFechar={() => { setNovo(false); setCriado(null); carregar(); }}>
        {novo && (criado
          ? <TokenCriado token={criado} aoFechar={() => { setNovo(false); setCriado(null); carregar(); }} />
          : <FormToken escopos={d?.escopos ?? Object.keys(NOME_ESCOPO)} aoCriar={setCriado} aoCancelar={() => setNovo(false)} />)}
      </Modal>
    </section>
  );
}

function FormToken({ escopos, aoCriar, aoCancelar }: { escopos: string[]; aoCriar: (t: string) => void; aoCancelar: () => void }) {
  const [nome, setNome] = useState('');
  const [marcados, setMarcados] = useState<string[]>(['chamados:registrar', 'chamados:consultar']);
  const [ips, setIps] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro(null);
    const lista = ips.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
    try {
      const r = await api<{ token: string }>('/tokens-servico', { metodo: 'POST', corpo: { nome: nome.trim(), escopos: marcados, ips_permitidos: lista.length ? lista : null } });
      aoCriar(r.token);
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível criar.'); }
  }
  return (
    <form className="form" onSubmit={enviar}>
      <label>Sistema<input required minLength={3} maxLength={80} value={nome} onChange={(e) => setNome(e.target.value)} placeholder='Ex.: "n8n WhatsApp", "Maza-Remote"' autoFocus /></label>
      <fieldset className="grupo-checks"><legend>Pode</legend>
        {escopos.map((s) => (
          <label key={s} className="check"><input type="checkbox" checked={marcados.includes(s)}
            onChange={(e) => setMarcados((m) => e.target.checked ? [...m, s] : m.filter((x) => x !== s))} /> {NOME_ESCOPO[s] ?? s}</label>
        ))}
      </fieldset>
      <label>Só aceitar destes IPs (opcional)<input value={ips} onChange={(e) => setIps(e.target.value)} placeholder="Ex.: 100.87.242.59" /></label>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={nome.trim().length < 3 || !marcados.length}><KeyRound size={18} aria-hidden /> Criar token</button>
      </div>
    </form>
  );
}

function TokenCriado({ token, aoFechar }: { token: string; aoFechar: () => void }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <div className="form">
      <p className="alerta alerta-aviso">Copie agora e guarde no sistema que vai usar. Ele <strong>não aparece de novo</strong>; se perder, revogue e crie outro.</p>
      <code className="token-criado">{token}</code>
      <p className="nota">Use no cabeçalho <code>Authorization: Bearer {'<token>'}</code>. Não mande por WhatsApp nem e-mail.</p>
      <div className="acoes-form">
        <button className="btn-fantasma" onClick={() => navigator.clipboard.writeText(token).then(() => setCopiado(true)).catch(() => {})}>{copiado ? 'Copiado' : 'Copiar'}</button>
        <button className="btn" onClick={aoFechar}>Já guardei</button>
      </div>
    </div>
  );
}
